// FE-SCRM-011 — React Query hooks for the Opportunity detail surface.
//
// The BOR-generate mutation writes the returned ``SalesActivity``
// straight into the activities cache so the timeline panel updates
// without a network refetch. Per spec: "After generation: link to
// download, button to 'Mark as Sent.'" — the link comes from the
// success payload; the activity is constructed locally from the
// returned ``activity_id`` plus a synthesized ``bor_letter_generated``
// stub so the timeline render is consistent with what a refetch
// would have produced.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  convertOpportunityToAccount,
  fetchOpportunityActivities,
  fetchOpportunityDetail,
  generateBorLetter,
} from "./api";
import type {
  ActivityListResponse,
  ConvertToAccountInput,
  ConvertToAccountResponse,
  GenerateBorLetterResponse,
  OpportunityDetail,
  SalesActivity,
} from "./types";


export const OPPORTUNITY_DETAIL_QUERY_KEY =
  "opportunity-detail" as const;
export const ACTIVITIES_QUERY_KEY = "opportunity-activities" as const;


export function useOpportunityDetailQuery(
  opportunity_id: string | undefined,
): UseQueryResult<OpportunityDetail> {
  return useQuery({
    queryKey: [OPPORTUNITY_DETAIL_QUERY_KEY, opportunity_id],
    queryFn: ({ signal }) =>
      fetchOpportunityDetail(opportunity_id!, { signal }),
    enabled: !!opportunity_id,
  });
}


export function useOpportunityActivitiesQuery(
  opportunity_id: string | undefined,
): UseQueryResult<ActivityListResponse> {
  return useQuery({
    queryKey: [ACTIVITIES_QUERY_KEY, opportunity_id],
    queryFn: ({ signal }) =>
      fetchOpportunityActivities(opportunity_id!, { signal }),
    enabled: !!opportunity_id,
  });
}


// BE-SCRM-032 / FE-SCRM-033 — Closed Won → Account conversion. On
// success we invalidate the opportunity detail query so the button
// flips to "View Account" without a full page refetch. The caller
// owns navigation (a useNavigate hook in the modal lands on
// ``/dashboard/accounts/{id}``).
export function useConvertToAccountMutation(
  opportunity_id: string,
): UseMutationResult<ConvertToAccountResponse, unknown, ConvertToAccountInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConvertToAccountInput) =>
      convertOpportunityToAccount(opportunity_id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [OPPORTUNITY_DETAIL_QUERY_KEY, opportunity_id],
      });
      qc.invalidateQueries({
        queryKey: [ACTIVITIES_QUERY_KEY, opportunity_id],
      });
    },
  });
}


export function useGenerateBorLetterMutation(
  opportunity_id: string,
): UseMutationResult<GenerateBorLetterResponse, unknown, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => generateBorLetter(opportunity_id),
    onSuccess: (result) => {
      // Optimistically append the returned activity to the timeline
      // cache. Reading the cache directly (rather than invalidating)
      // keeps the page reactive without a second network call —
      // matches the spec line "Display the activity in the timeline
      // immediately."
      const key = [ACTIVITIES_QUERY_KEY, opportunity_id];
      const now = new Date().toISOString();
      const stub: SalesActivity = {
        activity_id: result.activity_id,
        prospect_id: null,
        opportunity_id,
        contact_id: null,
        type: "bor_letter_generated",
        subject: `BOR letter generated (${result.filename})`,
        body: null,
        due_at: null,
        completed_at: now,
        owner_id: null,
        status: "completed",
        touchpoint_id: null,
        created_at: now,
        updated_at: now,
      };
      qc.setQueryData<ActivityListResponse>(key, (prev) => {
        if (!prev) return { rows: [stub], total: 1 };
        return { rows: [stub, ...prev.rows], total: prev.total + 1 };
      });
    },
  });
}
